import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useLang } from "../i18n/LangContext";
import { MaskLines, Eyebrow } from "../components/Reveal";
import { MetaphorStage } from "./Metaphors";
import { EASE } from "../lib/util";
import { api } from "@/lib/api";

const Stage = ({ active, caption }) => {
  const reduce = useReducedMotion();
  return (
    <div data-testid="capability-stage" className="capability-stage relative w-full overflow-hidden rounded-[32px] border border-noor-champagne/15 bg-noor-deep">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,rgba(171,127,62,0.18),transparent_65%)]" />
      {["start-4 top-4", "end-4 top-4", "start-4 bottom-4", "end-4 bottom-4"].map((p) => (
        <span key={p} className={`absolute h-3 w-3 border-noor-champagne/50 ${p} ${p.includes("top") ? "border-t" : "border-b"} ${p.includes("start") ? "border-s" : "border-e"}`} />
      ))}
      <AnimatePresence initial={false}>
        <motion.div key={active} className="absolute inset-[10%]" initial={reduce ? false : { opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} exit={reduce ? undefined : { opacity: 0, scale: 1.04 }} transition={{ duration: reduce ? 0 : 0.7, ease: EASE }}>
          <MetaphorStage index={active} />
        </motion.div>
      </AnimatePresence>
      <div className="absolute inset-x-6 bottom-6 flex items-end justify-between gap-4 text-xs text-noor-mist">
        <span>{caption}</span>
        <span className="font-brand text-noor-champagne" dir="ltr">{String(active + 1).padStart(2, "0")} / 06</span>
      </div>
    </div>
  );
};

export const Capabilities = () => {
  const { t, isFa } = useLang();
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  const [authState, setAuthState] = useState("checking");
  const cap = t.capabilities;
  const items = cap.items;
  const current = items[active];
  const checkingLabel = isFa ? "در حال بررسی ورود" : "Checking sign-in";
  const retryLabel = isFa ? "ورود بررسی نشد. دوباره تلاش کنید." : "Sign-in could not be checked. Try again.";

  const attempt = useRef(0);
  const checkSession = () => {
    const id = attempt.current + 1;
    attempt.current = id;
    setAuthState("checking");
    api.get("/auth/me").then(
      () => { if (attempt.current === id) setAuthState("in"); },
      (error) => { if (attempt.current === id) setAuthState(error?.response?.status === 401 ? "out" : "unavailable"); }
    );
  };

  useEffect(() => {
    checkSession();
    return () => { attempt.current += 1; };
  }, []);

  const select = (index) => {
    if (index === active) return;
    setActive(index);
    setOpen(false);
  };

  const sessionAction = authState === "in"
    ? <a href="/workspace/strategist" className="capability-cta" data-testid="capability-cta" data-auth-state="in">{cap.cta}</a>
    : authState === "out"
      ? <a href="/register" className="capability-cta" data-testid="capability-cta" data-auth-state="out">{cap.cta}</a>
      : authState === "checking"
        ? <button type="button" className="capability-cta is-pending" data-testid="capability-cta" data-auth-state="checking" aria-busy="true" aria-label={checkingLabel} disabled>{cap.cta}</button>
        : <button type="button" className="capability-cta" data-testid="capability-retry" data-auth-state="unavailable" onClick={checkSession}>{retryLabel}</button>;

  return (
    <section id="services" data-testid="capabilities-section" className="relative z-[2] bg-noor-emerald py-20 md:py-32">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <Eyebrow>{cap.eyebrow}</Eyebrow>
            <MaskLines lines={cap.lines} className="display-lg mt-6 font-display text-noor-pearl" lineClassName="italic-accent text-noor-champagne" testId="capabilities-headline" />
          </div>
          <p className="max-w-xs text-sm leading-relaxed text-noor-mist">{cap.hint}</p>
        </div>
        <div className="mt-12 grid gap-8 lg:mt-16 lg:grid-cols-12 lg:gap-16">
          <div className="lg:order-2 lg:col-span-7">
            <div className="lg:sticky lg:top-28">
              <Stage active={active} caption={current.caption} />
            </div>
          </div>
          <ul className="lg:col-span-5">
            {items.map((item, index) => {
              const selected = index === active;
              return (
                <li key={item.title} className="border-t border-noor-pearl/10 last:border-b">
                  <button
                    type="button"
                    data-testid={`capability-item-${index}`}
                    aria-pressed={selected}
                    onFocus={() => select(index)}
                    onClick={() => select(index)}
                    className="group flex w-full items-start gap-5 py-5 text-start focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-noor-champagne"
                  >
                    <span className={`mt-2 font-brand text-xs ${selected ? "text-noor-champagne" : "text-noor-mist/70"}`} dir="ltr">{String(index + 1).padStart(2, "0")}</span>
                    <span className="min-w-0 flex-1">
                      <span className={`block font-display text-2xl leading-snug md:text-[1.7rem] ${selected ? "text-noor-pearl" : "text-noor-pearl/60 group-hover:text-noor-pearl/85"}`}>{item.title}</span>
                      {selected && <span className="mt-3 block text-[15px] leading-8 text-noor-mist">{item.text}</span>}
                    </span>
                    <span className={`mt-3 h-px ${selected ? "w-10 bg-noor-champagne" : "w-4 bg-noor-pearl/25"}`} />
                  </button>
                  {selected && (
                    <div className="mb-5 ps-9">
                      <button type="button" className="capability-detail-toggle" data-testid="capability-details-toggle" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
                        {open ? cap.detailsClose : cap.details}
                      </button>
                      {open && (
                        <div className="capability-details" data-testid="capability-details">
                          <p className="text-xs tracking-wide text-noor-champagne">{cap.servicesLabel}</p>
                          <ul className="capability-services">
                            {item.services.map((service) => <li key={service}>{service}</li>)}
                          </ul>
                          <p className="mt-4 text-xs text-noor-champagne">{cap.outputsLabel}</p>
                          <p className="mt-2 text-sm leading-7 text-noor-pearl/85">{item.outputs}</p>
                          <p className="mt-3 text-xs leading-6 text-noor-mist">{cap.scopeNote}</p>
                          {sessionAction}
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
};
